import { zodResolver } from '@hookform/resolvers/zod';
import { PASSWORD_MIN_LENGTH, resetPasswordSchema } from '@i9portal/shared';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { Alert } from '../../components/alert';
import { ErrorMessage } from '../../components/error-state';
import { Field, fieldA11y } from '../../components/field';
import { useDocumentTitle } from '../../hooks/use-document-title';
import { authApi } from '../../lib/api/endpoints/auth';
import { applyApiErrorToForm } from '../../lib/forms';

interface ResetPasswordPageProps {
  /** `activate`: convite de acesso (/ativar-conta). `reset`: redefinição (/redefinir-senha). */
  mode: 'reset' | 'activate';
}

export function ResetPasswordPage({ mode }: ResetPasswordPageProps) {
  const title = mode === 'activate' ? 'Ativar conta' : 'Redefinir senha';
  useDocumentTitle(title);
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const navigate = useNavigate();
  const [submitError, setSubmitError] = useState<unknown>(null);
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { token, password: '', passwordConfirmation: '' },
  });

  if (!token) {
    return (
      <>
        <h1 className="public__title">{title}</h1>
        <Alert tone="danger" title="Link inválido">
          O link utilizado está incompleto ou expirou. Solicite um novo link.
        </Alert>
        <p className="public__links">
          <Link to="/esqueci-senha">Solicitar novo link</Link>
        </p>
      </>
    );
  }

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null);
    try {
      await authApi.resetPassword(values);
      navigate('/login', {
        replace: true,
        state: {
          message:
            mode === 'activate'
              ? 'Conta ativada! Entre com seu e-mail e a senha cadastrada.'
              : 'Senha redefinida com sucesso. Entre com a nova senha.',
        },
      });
    } catch (error) {
      if (!applyApiErrorToForm(error, setError)) setSubmitError(error);
    }
  });

  const hint = `Mínimo de ${PASSWORD_MIN_LENGTH} caracteres, com letras e números.`;

  return (
    <>
      <h1 className="public__title">{title}</h1>
      <p className="muted">
        {mode === 'activate'
          ? 'Bem-vindo ao i9Portal! Defina sua senha para ativar o acesso.'
          : 'Escolha uma nova senha para sua conta.'}
      </p>
      <form className="stack" onSubmit={onSubmit} noValidate>
        <input type="hidden" {...register('token')} />
        <Field
          label="Nova senha"
          htmlFor="reset-password"
          error={errors.password?.message}
          hint={hint}
          required
        >
          <input
            type="password"
            className="input"
            autoComplete="new-password"
            autoFocus
            {...fieldA11y('reset-password', errors.password?.message, hint)}
            {...register('password')}
          />
        </Field>
        <Field
          label="Confirme a nova senha"
          htmlFor="reset-password-confirmation"
          error={errors.passwordConfirmation?.message}
          required
        >
          <input
            type="password"
            className="input"
            autoComplete="new-password"
            {...fieldA11y('reset-password-confirmation', errors.passwordConfirmation?.message)}
            {...register('passwordConfirmation')}
          />
        </Field>
        {errors.token?.message && <Alert tone="danger">O link é inválido ou expirou.</Alert>}
        {submitError ? <ErrorMessage error={submitError} /> : null}
        <button type="submit" className="btn btn--primary btn--block" disabled={isSubmitting}>
          {isSubmitting ? 'Salvando…' : mode === 'activate' ? 'Ativar conta' : 'Redefinir senha'}
        </button>
      </form>
      <p className="public__links">
        <Link to="/login">Voltar para o login</Link>
      </p>
    </>
  );
}
